/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace variable-name align max-line-length */

import {ProjectElement}         from './project.element';
import {ProjectElementType}     from './project.element.type';
import {Project}                from '../project';

import {
  Blob as FileBlob,
  BlobType
}                               from '../blob';

import {Logger}                 from '@/util/logger';
import {basename, empty} from "@/util/util";
import {KPI}                    from '@/models/k.p.i';
import {JSONBrowserMetadata}    from '@/screens/ide/json.browser.metadata';
import {KPITableViewerMetadata} from '@/screens/ide/k.p.i.table.viewer.metadata';

import {first}                  from 'rxjs/operators';
import {ProjectElementFactoryDecorator} from "@/models/project/project.element.factory.decorator";
import {FileBrowserMetadata} from "@/screens/ide/file.browser.metadata";


/**
 *
 * @class KPITableElement
 *
 * @brief node to aggregate multiple KPI holders into a "table" we can then visual in agGrid or a graph.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
@ProjectElementFactoryDecorator('KPITableElement')
export class KPITableElement extends ProjectElement {

  /** where we saved this table as a blob */
  public kpiTableKey: string = '';

  /** unique number for all KPI tables in the project */
  public instance: number = 0;

  /** when the table was created */
  public created: Date = null;

  /** actions for this element */
  protected actionKeys: string[] = [
    'editkpitable',
    'jsoneditkpitable',
    'downloadkpitable',
    'renamekpitable',
    'deletekpitable',
    'viewinbrowser'
  ];

  /** standard constructor */
  constructor(
    project:  Project,
    id:       string,
    instance: number,
    data:     KPI[],
    created:  Date,
    tableKey: string,
    parent:   ProjectElement,
    publish?: boolean,
    peType?:  ProjectElementType
  ) {

    super(project, id, peType ? peType : ProjectElementType.KPITable);

    this.created      = created;
    this.instance     = instance;
    this.kpiTableKey  = tableKey;
    this._enabled     = true;
    this._label       = `#${instance} KPI Table ` + this.created.toISOString();
    this._icon        = 'far fa-chart-bar';
    this._elementKind = 'KPITable';

    if (parent) {

      this._project.addElement(this, parent, publish);
    }

    /* save this table to a blob so we can easily pass it around by blob reference */

    if( !empty(tableKey)) {

      /* stringify works because KPI has a toJSON() method */
      const content: string = JSON.stringify(data);

      project.services.blobber.create(tableKey, BlobType.File, content, 'kpi table', 'kpi table')
        .pipe(first())
        .subscribe(
          created => {

            Logger.debug('[KPITableElement] create KPI table: ', tableKey);

          },
          (error) => {

            Logger.error('[KPITableElement] could not create KPI table', error);
          });

    }
  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring KPITableElement");
  }

  /** drop data on this element */
  public acceptDrop(data): boolean {

    return false;
  }

  /** is this element deletable? */
  public isDeletable() : boolean {

    return true;
  }

  /** test if any blob of this type can be a child of this element */
  public acceptsBlobType(type: BlobType): boolean {

    return false;
  }
  /** convert the KPI data array to a JSON string */
  public dataToString(items: KPI[]): string[] {

    const kpiStrings: string[] = [];

    items.forEach((item: KPI) => {

      kpiStrings.push(item.toJSON());
    });

    return kpiStrings;
  }

  /** convert a JSON string back to a KPI data array */
  public stringToData(items: string[]): KPI[] {

    const results: KPI[] = [];

    items.forEach((item: string) => {

      const kpi = new KPI({});

      /* recover this KPI holder ... */

      kpi.fromJSON(item);

      results.push(kpi);

    });

    return results;
  }

  /** convert this element to a simplified form that can be easily serialized */
  public node(init?: any) {

    return super.node({
      created:     JSON.stringify(this.created),
      instance:    this.instance,
      kpitablekey: this.kpiTableKey
    });
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any) {

    /* recover the basics */

    super.fromNode(node);

    /* recover the job details */

    this.instance    = node.instance;
    this.created     = new Date(JSON.parse(node.created));
    this.kpiTableKey = node.kpitablekey;

    if (empty(this._label)) {

      this._label = `#${this.instance} KPI Table ` + this.created.toISOString();
    }
  }

  /**
   * sub-classes must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    actionSet.editkpitable = {
      name: 'Edit',
      icon: 'far fa-chart-bar',
      disabled: false
    };

    actionSet.jsoneditkpitable = {
      name: 'JSON View',
      icon: 'fas fa-keyboard',
      disabled: false
    };

    actionSet.downloadkpitable = {
      name: 'Download',
      icon: 'fas fa-file-download',
      disabled:  false
    };

    actionSet.viewinbrowser = {
      name: 'Folder Of',
      icon: 'fas fa-list',
      disabled: false
    };

    actionSet.renamekpitable = {
      name: 'Rename Table',
      icon: 'fas fa-tag',
      disabled: false
    };

    actionSet.deletekpitable = {
      name: 'Delete Table',
      icon: 'fas fa-trash',
      disabled: false
    };

    return actionSet;
  }

  /** test if this element accepts the given action */
  public acceptsAction(key: string): boolean {

    return !!this.actionKeys.find(x => x === key);
  }

  /** sub-classes must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    if ( !this.actionKeys.find(x => x === key) ) {

      /* let ProjectElement handle it */

      this._parent.action(key, selection);

      return ;
    }

    switch (key) {

      case 'editkpitable': {

        Logger.debug('[KPITableElement] edit');

        this._project.services.ide.launchWindow(new  KPITableViewerMetadata(
          '',
          '',
          this.kpiTableKey,
          '<i class="far fa-chart-bar"></i>'
        ));
      }
      break;

      case 'jsoneditkpitable': {

        Logger.debug('[KPITableElement] JSON view');

        this._project.services.ide.launchWindow(new  JSONBrowserMetadata(
          '',
          '',
          this.kpiTableKey,
          'json',
          basename(this.kpiTableKey),
          '<i class="fas fa-edit"></i>'
        ));

      }
      break;

      case 'downloadkpitable': {

        Logger.debug('[KPITableElement] downloading');

        this.downloadKPITable();

      }
      break;

      case 'viewinbrowser': {

        this._project.services.ide.launchWindow(FileBrowserMetadata.build(this.kpiTableKey, this.kpiTableKey));

        break;
      }

      case 'renamekpitable': {

        /* Similar to KPI element, just allow custom label after the #id */

        const kpt: KPITableElement = this;

        kpt._project.services.modals.open(
          'rename-element-modal',
          (name, resolve, reject) => {

            name = `#${kpt.instance} ${name.trim()}`;

            Logger.debug('[KPITableElement] validating: ', name);

            /* make sure we aren't name clashing ...  */

            const peers: ProjectElement[] = kpt._parent._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('KPI table of that name already exists.');

                return;
              }

            }

            resolve(name);

          },
          (name) => {

            /* new label was accepted */

            Logger.debug('[KPITableElement] renaming: ', name);

            kpt._label = `${name.trim()}`;

            kpt.redraw();

            kpt.save();

          }, {
            title: 'Rename KPI Table',
            placeholder: this._label
          });

      }
      break;

      case 'deletekpitable': {

        Logger.debug('[KPITableElement] deleting');

        this.deleteElements(selection);
      }
      break;

    }

  }

  public downloadKPITable() {

    Logger.debug('[KPITableElement] downloading', this.kpiTableKey, this._blobType);

    let   mime: string   = 'application/json';

    let keyBase = this.kpiTableKey.substring(this.kpiTableKey.lastIndexOf('/') + 1);

    if (!keyBase || keyBase === '' ) {

      keyBase = this._blobKey;
    }

    /* fetch the blob */

    this._project.services.blobber.contents(this.kpiTableKey)
      .pipe(first())
      .subscribe(
        (content: string) => {

          Logger.debug(`[KPITableElement] downloading ${keyBase} => ${mime}`);

          const blob = new Blob([content], { type: mime });
          const url  = window.URL.createObjectURL(blob);

          /* setup a fake one-time link with the right download name and the blob attached as the download */

          const element: HTMLAnchorElement = document.createElement('a');
          element.href          = url;
          element.download      = keyBase || 'download';

          element.style.display = 'none';

          document.body.appendChild(element);

          /* do it */

          element.click();

          /* clean up */

          document.body.removeChild(element);

        },
        (error) => {

          Logger.error(`[KPITableElement] can't find content ${this._blobKey}: `, error);
        });

  }

  /*
   * when blobs get moved/copied in bulk, project elements have to allow being told where
   * their blobs have been moved to.
   *
   */
  public adjusted(searchKey: string, replaceKey: string) {

    if (this.kpiTableKey === searchKey) {

      this.kpiTableKey = replaceKey;
    }

    super.adjusted(searchKey, replaceKey);
  }

  /** fetch any secondary blobs for this element */
  public secondaryBlobs(): string[] {

    if ((this.kpiTableKey === null) || (this.kpiTableKey === '')) {

      return [];
    }

    /* KPI table elements have a blob for the aggregated KPI */

    return [
      this.kpiTableKey
    ];
  }

  /** helper to delete any cached artifacts from the remote simulation */
  public deleteKPITable(selection: ProjectElement[]) {

    if (this.pendingKeys.length > 0) {

      /* busy right now */

      return ;
    }

    /*
     * the cached results are internal to a "result", we just need to batch them up,
     * and remove them all with a fork/join.  In our case there are just two artifacts,
     * the log and the output.
     *
     */

    this.pendingKeys = [];

    this.pendingKeys.push([this.kpiTableKey,    'latest']);

    Logger.debug('[KPITableElement] deleting blobs: ', this.pendingKeys);

    this._project.services.blobber.remove(this.pendingKeys[0][0], this.pendingKeys[0][1])
      .pipe(first())
      .subscribe(
        (results: string) => {

          /* everything got deleted ok, add the children in the project and save the latest project */

          Logger.debug('[KPITableElement] blobs deleted: ', results);

          /* make sure the project is saved */

          this.pendingKeys = [];

          /* delete self */

          this.deleteElements([this]);

          /* trigger a save of the project */

          this.save();
        },
        (error) => {

          Logger.error('[KPITableElement] problems deleting blobs: ', error);

        });

  }


}
